console.debug("LOADING AppFeatures")
import { rootStore } from "store/RootStore"
import { ISDEV } from "utils/TestSupport"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { flow, makeObservable, computed, observable, action } from "mobx"

class AppFeatures {
   // TODO: why does 'readonly' generate an error?
   _gt12Enabled: boolean = false
   _testButtonsEnabled: boolean = true
   _pinResetEnabled: boolean = true
   // Alpha release: Options for enabling bookmarks commented in Settings
   _bookmarksEnabled: boolean = true // Alpha release: set to true
	_thumbnailsEnabled: boolean = true
	_recordingsInSearchEnabled: boolean = true
   _coco_available: boolean = true // Alpha release: set to true
   _coco_login = true
   _traxisOn = true
	_radioEnabled = true
	_locale = "de"
	_isAdultContentProtected = true
	_showAdultContent = false

   TRAXIS_DEFAULT_TIMEOUT = 40000
   TRAXIS_DEFAULT_RETRIES = 1

	constructor() {
		makeObservable(this, {
			persistedLanguage: computed,
			_isAdultContentProtected: observable,
			_showAdultContent: observable,
			isAdultContentProtected: computed,
			showAdultContent: computed,
			applyPersistentSettings: action,
			setPersistedAdultPinProtected: action,
			setPersistedAdultContent: action,
			gt12Enabled: computed,
		})
	}

	applyPersistentSettings() {
		// define this._locale
		const dummy = this.persistedLanguage
		//const test =  rootStore.sso.profile
		this._isAdultContentProtected = this.persistedAdultPinProtect
		// will fetch channels with or without adult content based on the setting
		this.showAdultContent = this.persistedAdultContent
	}

   get traxisAvailable() {
      return this._traxisOn && this.persistedTraxisOption
   }
   get coCoAvailable() {
      //console.debug("coCoAvailable", this._coco_available || this.persistedBookmarks)
      // serviceUlrs are defined after authentication
      return this._coco_available || this.persistedBookmarks
   }

   get coCoLoginEnabled() {
      return this._coco_login
   }

   get bookmarksEnabled() {
      //console.debug("bookmarksEnabled", this._bookmarksEnabled || this.persistedBookmarks)
      // serviceUlrs are defined after authentication
      return this._bookmarksEnabled || this.persistedBookmarks
   }

	get radioEnabled() {
		return this._radioEnabled
	}

	get thumbnailsEnabled() {
		return this._thumbnailsEnabled
	}

	get showThumbnails() {
		return this.thumbnailsEnabled && this.persistedShowThumbnails
	}

	get recordingsInSearchEnabled() {
		return this._recordingsInSearchEnabled
	}

	// MTVW-1055: language setting
	get languageSelectionEnabled(): boolean {
		//return ISDEV
		return true
	}

	get persistedLanguage(): string {
      const persistedState = rootStore?.sso?.browserStore?.getLanguage()
		this._locale = persistedState
      return persistedState
   }

   set persistedLanguage(value: string) {
      rootStore.sso.browserStore.setLanguage(value)
		this._locale = value
   }

	// MTVW-1094: Adult content
	get persistedAdultPinProtect(): boolean {
		// use setting from profile
		//const persistedState = rootStore?.sso?.browserStore?.getAdultPinProtect()
		const persistedState = rootStore.sso.profile?.AdultAuthentication
		return persistedState
	}

	setPersistedAdultPinProtected = flow(function* (value: boolean, pin: string) {
		// update setting in profile
		//rootStore?.sso?.browserStore?.setAdultPinProtect(value)
		rootStore.sso.profile.setAdultAuthentication(value, pin).then(() => {
			this._isAdultContentProtected = value
		}).catch((error) => {
			console.error("Failed to update adult content pin protection", error)
		})
	})

	get persistedAdultContent(): boolean {
		// use setting from profile
		//const persistedState = rootStore?.sso?.browserStore?.getAdultContent()
		const persistedState = rootStore.sso.profile?.AdultContentAccessShown
		return persistedState
	}

	setPersistedAdultContent = flow(function* (value: boolean, pin: string) {
		// update setting in profile
		//rootStore?.sso?.browserStore?.setAdultContent(value)
		rootStore.sso.profile.setAdultContentAccessShown(value, pin).then(() => {
			// will fetch channels with or without adult content based on the new setting
			this.showAdultContent = value
		}).catch((error) => {
			console.error("Failed to update adult content access shown", error)
		})
	})

	get isAdultContentProtected(): boolean {
		return this._isAdultContentProtected
   }

   set isAdultContentProtected(value: boolean) {
		this._isAdultContentProtected = value
   }

	get showAdultContent(): boolean {
		return this._showAdultContent
   }

   set showAdultContent(value: boolean) {
		this._showAdultContent = value
		// get channels to apply filter
		if (this._showAdultContent !== undefined && this._showAdultContent !== null) {
			rootStore.channelsService.getChannelsAsync(this._showAdultContent)
		}
   }

	get persistedNextEpisode(): boolean {
		const persistedState = rootStore?.sso?.browserStore?.getShowNextEpisode()
		return persistedState
	}

	set persistedNextEpisode(value: boolean) {
		rootStore?.sso?.browserStore?.setShowNextEpisode(value)
	}

	get persistedShowThumbnails(): boolean {
		const persistedState = rootStore?.sso?.browserStore?.getShowThumbnails()
		return persistedState
	}

	set persistedShowThumbnails(value: boolean) {
		rootStore?.sso?.browserStore?.setShowThumbnails(value)
	}

	get locale(): string {
		return this._locale
	}

   // MTVW-540
   setGt12EnabledState(enabled: boolean) {
      const persistedState = rootStore?.sso?.browserStore?.gt12State
      if (persistedState !== undefined) this._gt12Enabled = persistedState
      this._gt12Enabled = this._gt12Enabled || enabled
      //console.debug("persisted %s, loginMs %s, result %s", persistedState, enabled, this._gt12Enabled)
	}

   overrideGt12State(enabled: boolean) {
      this._gt12Enabled	= enabled
      rootStore.sso.browserStore.setGt12State(enabled)
	}

	get settingsGt12StateEnabled(): boolean {
      return /*this.gt12Enabled &&*/ ISDEV
   }

   get gt12Enabled(): boolean {
      const persistedState = rootStore?.sso?.browserStore?.gt12State
      //console.debug("persistedState %s, gt12Enabled %s, result %s", persistedState, this._gt12Enabled, persistedState || this._gt12Enabled)
      return persistedState || this._gt12Enabled
   }

   get settingsBookmarksEnabled(): boolean {
      return ISDEV
   }

   get persistedBookmarks(): boolean {
      const persistedState = rootStore?.sso?.browserStore?.bookmarks
      return persistedState
   }

   set persistedBookmarks(value: boolean) {
      rootStore.sso.browserStore.setBookmarks(value)
   }

   get settingsAdsMockEnabled(): boolean {
      return this.gt12Enabled && ISDEV
   }

   get persistedAdsMock(): boolean {
      return this._gt12Enabled && rootStore.sso.browserStore.adsMock
   }

   set persistedAdsMock(value: boolean) {
      rootStore.sso.browserStore.setAdsMock(value)
   }

   get settingsTestButtonsEnabled(): boolean {
      return this.gt12Enabled && ISDEV
   }

   get persistedTestButtons(): boolean {
      //console.debug("persistedTestButtons", this._gt12Enabled ? rootStore.sso.browserStore.testButtons : false)
      return this._gt12Enabled ? rootStore.sso.browserStore.testButtons : false
   }

   set persistedTestButtons(value: boolean) {
      rootStore.sso.browserStore.setTestButtons(value)
   }

   get settingsAdsTimeoutEnabled(): boolean {
      //console.debug("persistedTestButtons", this._gt12Enabled ? rootStore.sso.browserStore.testButtons : false)
      return this._gt12Enabled && ISDEV
   }

   get persistedAdsTimeout(): number {
      return rootStore.sso.browserStore.adsTimeout
   }

   get isDefaultAdsTimeout(): boolean {
      return rootStore.sso.browserStore.adsTimeout === rootStore.sso.browserStore.DEFAULT_ADSTIMEOUT
   }

   set persistedAdsTimeout(value: number) {
      rootStore.sso.browserStore.setAdsTimeout(value)
   }

   set defaultAdsTimeout(normal: boolean) {
      rootStore.sso.browserStore.setAdsTimeout(normal ? rootStore.sso.browserStore.DEFAULT_ADSTIMEOUT : rootStore.sso.browserStore.HIGH_ADSTIMEOUT)
   }

   // MTVW-593, MTVW-848: NTL
   get settingsNtlEnabled() {
      return ISDEV
   }

   get persistedNtl(): boolean {
      return rootStore.sso.browserStore.ntl
   }

   set persistedNtl(value: boolean) {
      rootStore.sso.browserStore.setNtl(value)
   }

   get settingsTraxisEnabled() {
       return ISDEV
   }

   get persistedTraxisOption(): boolean {
      return rootStore.sso.browserStore.traxisOption
   }

   set persistedTraxisOption(value: boolean) {
      rootStore.sso.browserStore.setTraxisOption(value)
   }

   get settingsOnboardingEnabled(): boolean {
      return this.gt12Enabled && ISDEV
   }

   // for BDD test purpose
   get persistedOnboardingOption() : number {
      return rootStore.sso.browserStore.onboardingOption
   }

   // for BDD test purpose
   set persistedOnboardingOption(value: number) {
      rootStore.sso.browserStore.setOnboardingOption(value)
   }

   get settingsPinResetEnabled(): boolean {
      //return this._pinResetEnabled && ISDEV
      // disabled since v3.2206.01
      return false
   }

   get persistedPinReset(): boolean {
      //return rootStore.sso.browserStore.pinReset
      // enabled since v3.2206.01
      return true
   }

   set persistedPinReset(value: boolean) {
      rootStore.sso.browserStore.setPinReset(value)
   }

   get settingsVConsoleEnabled(): boolean {
       return ISDEV
   }

   get settingsPlayerLogEnabled(): boolean {
       return ISDEV
   }

   get persistedVConsole(): boolean {
      return rootStore.sso.browserStore.vConsole
   }

   set persistedVConsole(value: number) {
      rootStore.sso.browserStore.setVConsole(value)
   }

   get persistedPlayerLog(): boolean {
      return rootStore.sso.browserStore.playerLog
   }

   set persistedPlayerLog(value: number) {
      rootStore.sso.browserStore.setPlayerLog(value)
   }

   // MTVW-920
   checkTraxisOnStartup = flow(function* (this: any): any {
      // set below to true for real world traxis communitcation check
      const simulateTraxisDown = !this.persistedTraxisOption
      const defaultTimeout = this.TRAXIS_DEFAULT_TIMEOUT
      const defaultRetries = this.TRAXIS_DEFAULT_RETRIES
      const defaultTraxisUrl = serviceUrls.traxis
      if (simulateTraxisDown) {
         this.persistedTraxisOption = true
         //https://apps-traxis-api.quickline.ch/traxis/web/?output=json&language=de
         serviceUrls.traxis = serviceUrls.traxis.replace("quickline.ch", "quickline_x.com")
         //serviceUrls.traxis = "https://undefxxxxx.com"
         //console.debug("checkTraxisOnStartup", this.traxisAvailable, serviceUrls.traxisUrl)
      }
      try {
         // very first traxis call, if traxis fails test?.traxisResponse is not available
         // reduce timeout and retries temporarily
         this.TRAXIS_DEFAULT_TIMEOUT = 5000
         this.TRAXIS_DEFAULT_RETRIES = 0
         const test = yield rootStore.time.handleSyncDateFromServerAsync()
         this.TRAXIS_DEFAULT_TIMEOUT = defaultTimeout
         this.TRAXIS_DEFAULT_RETRIES = defaultRetries
         // comment below block for real world traxis communitcation check
         if (simulateTraxisDown) {
            this.persistedTraxisOption = false
         }
         // uncomment below line for real world traxis communitcation check
         //serviceUrls.traxis = defaultTraxisUrl
         // set traxis off if no response
         if (!test?.traxisResponse) this._traxisOn = false
         console.debug("test?.traxisResponse", test?.traxisResponse)
         // fetch channels, predefined channel lists in GetChannelLists need them
			// MTVW-1013
			yield rootStore.channelsService.init()
         // ignore below TypeScript warning
         return test?.traxisResponse
      }
      catch (e) {
         console.error("checkTraxisOnStartup", e)
         return null
      }
   })
}

export const appFeatures = new AppFeatures()
